import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Copy, RefreshCw, Users } from 'lucide-react';
import {
  getMyMembers,
  getMyMembership,
  MemberItem,
  MyMembership,
  regenerateMembershipKey,
  saveMembership,
} from '../../../api/payment/membership.api';
import { formatCa } from '../../../utils/live-effect';
import { formatMemberUntil } from '../../../utils/membership';

/**
 * Khối "Hội viên kênh" trong Cài đặt Donate: streamer bật hội viên, chọn giá tháng, đặt mục tiêu,
 * mức giảm giá hiệu ứng, lấy link lớp phủ OBS, xem thống kê và danh sách hội viên.
 */

const errorMessage = (
  err: unknown,
  fallback = 'Không lưu được, thử lại sau.'
) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data
    ?.message || fallback;

const inputCls =
  'w-full rounded-md border ui-divider bg-transparent px-2 py-1 text-[13px] outline-none ui-text';

const parseDraft = (raw: string) => (raw.trim() === '' ? NaN : Number(raw));

const NumberInput: React.FC<{
  label: string;
  value: number;
  min?: number;
  max?: number;
  onChange: (next: number) => void;
}> = ({ label, value, min, max, onChange }) => {
  const [text, setText] = useState(Number.isFinite(value) ? String(value) : '');
  useEffect(() => {
    setText((prev) =>
      Object.is(parseDraft(prev), value)
        ? prev
        : Number.isFinite(value)
        ? String(value)
        : ''
    );
  }, [value]);
  return (
    <input
      aria-label={label}
      type="number"
      inputMode="numeric"
      min={min}
      max={max}
      className={inputCls}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onChange(parseDraft(e.target.value));
      }}
    />
  );
};

const isInt = (n: number) => Number.isInteger(n);

/** Kiểm trước khi gửi, chữ giống máy chủ. Trả về câu lỗi đầu tiên hoặc null. */
const validate = (m: MyMembership): string | null => {
  if (!isInt(m.priceCa) || !m.priceOptions.includes(m.priceCa))
    return 'Giá không hợp lệ.';
  if (!isInt(m.goalTarget) || m.goalTarget < 0 || m.goalTarget > 100000)
    return 'Mục tiêu hội viên phải từ 0 đến 100.000.';
  if (
    !isInt(m.effectDiscountPercent) ||
    m.effectDiscountPercent < 0 ||
    m.effectDiscountPercent > 50
  )
    return 'Mức giảm hiệu ứng phải từ 0 đến 50%.';
  return null;
};

const MembershipSetting: React.FC<{ userId?: string }> = ({ userId }) => {
  const [mine, setMine] = useState<MyMembership | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [members, setMembers] = useState<MemberItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(() => {
    setLoadFailed(false);
    getMyMembership()
      .then(setMine)
      .catch(() => setLoadFailed(true));
    getMyMembers(1)
      .then((res) => {
        setMembers(res?.items ?? []);
        setTotal(res?.total ?? 0);
        setPage(1);
      })
      .catch(() => setMembers([]));
  }, []);
  useEffect(load, [load]);

  if (!mine) {
    if (!loadFailed) return null;
    return (
      <div className="mt-6 box rounded-2xl p-4 md:p-6 text-[14px] ui-text">
        <p>Không tải được cài đặt hội viên.</p>
        <button
          type="button"
          className="mt-2 rounded-md border ui-divider px-3 py-1 text-[13px] ui-text"
          onClick={load}
        >
          Thử lại
        </button>
      </div>
    );
  }

  const overlayUrl =
    mine.overlayKey && userId
      ? `${window.location.origin}/member-screen/${userId}?key=${mine.overlayKey}`
      : '';

  const patch = (p: Partial<MyMembership>) => {
    setFormError('');
    setMine({ ...mine, ...p });
  };

  const save = () => {
    if (saving) return;
    const invalid = validate(mine);
    setFormError(invalid ?? '');
    if (invalid) {
      toast.error(invalid);
      return;
    }
    setSaving(true);
    saveMembership({
      isEnabled: mine.isEnabled,
      priceCa: mine.priceCa,
      goalTarget: mine.goalTarget,
      effectDiscountPercent: mine.effectDiscountPercent,
      showRecentTicker: mine.showRecentTicker,
    })
      .then((res) => {
        setMine(res);
        toast.success('Đã lưu cài đặt hội viên.');
      })
      .catch((err) => toast.error(errorMessage(err)))
      .finally(() => setSaving(false));
  };

  const loadMore = () => {
    if (loadingMore) return;
    setLoadingMore(true);
    getMyMembers(page + 1)
      .then((res) => {
        setMembers((prev) => [...prev, ...(res?.items ?? [])]);
        setTotal(res?.total ?? total);
        setPage(page + 1);
      })
      .catch((err) =>
        toast.error(errorMessage(err, 'Không tải được thêm, thử lại sau.'))
      )
      .finally(() => setLoadingMore(false));
  };

  const s = mine.stats;

  return (
    <div className="mt-6 box rounded-2xl p-4 md:p-6">
      <div className="flex items-center gap-2">
        <Users className="text-blue-600" size={20} />
        <h3 className="text-lg font-semibold ui-heading">Hội viên kênh</h3>
      </div>
      <p className="mt-1 text-[13px] ui-muted">
        Người xem trả Ca hằng tháng hoặc dùng lượt Cab+ để thành hội viên của
        bạn. {`Bạn nhận ${mine.creatorSharePercent ?? 70}% giá.`}
      </p>

      <label className="mt-3 flex items-center gap-2 text-[14px] ui-text">
        <input
          type="checkbox"
          checked={mine.isEnabled}
          onChange={(e) => patch({ isEnabled: e.target.checked })}
        />{' '}
        Bật hội viên kênh
      </label>

      <div className="mt-3">
        <span className="text-[12px] ui-muted">Giá hội viên mỗi tháng</span>
        <div className="mt-1 flex flex-wrap gap-2">
          {mine.priceOptions.map((p) => (
            <button
              key={p}
              type="button"
              aria-pressed={mine.priceCa === p}
              onClick={() => patch({ priceCa: p })}
              className={`rounded-md border px-3 py-1 text-[13px] ${
                mine.priceCa === p
                  ? 'border-[#0057FF] bg-[#0057FF] text-white'
                  : 'ui-divider ui-text'
              }`}
            >
              {formatCa(p)}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <label className="text-[12px] ui-muted">
          Mục tiêu số hội viên (0 là ẩn thanh mục tiêu)
          <NumberInput
            label="Mục tiêu số hội viên (0 là ẩn thanh mục tiêu)"
            min={0}
            max={100000}
            value={mine.goalTarget}
            onChange={(goalTarget) => patch({ goalTarget })}
          />
        </label>
        <label className="text-[12px] ui-muted">
          Giảm giá hiệu ứng cho hội viên (%)
          <NumberInput
            label="Giảm giá hiệu ứng cho hội viên (%)"
            min={0}
            max={50}
            value={mine.effectDiscountPercent}
            onChange={(effectDiscountPercent) =>
              patch({ effectDiscountPercent })
            }
          />
        </label>
      </div>

      <label className="mt-3 flex items-center gap-2 text-[14px] ui-text">
        <input
          type="checkbox"
          checked={mine.showRecentTicker}
          onChange={(e) => patch({ showRecentTicker: e.target.checked })}
        />{' '}
        Hiện dải tên hội viên gần đây trên lớp phủ
      </label>

      {formError && (
        <p role="alert" className="mt-3 text-[13px] text-red-600">
          {formError}
        </p>
      )}
      <button
        type="button"
        disabled={saving}
        onClick={save}
        className="mt-4 rounded-lg bg-[#0057FF] px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-60"
      >
        {saving ? 'Đang lưu…' : 'Lưu cài đặt'}
      </button>

      <div className="mt-4 flex flex-col gap-1 text-[13px]">
        <span className={mine.overlayOnline ? 'text-emerald-600' : 'ui-muted'}>
          {mine.overlayOnline
            ? 'Lớp phủ hội viên: đang chạy'
            : 'Lớp phủ hội viên: chưa mở'}
        </span>
      </div>
      {overlayUrl && (
        <div className="mt-2 flex min-w-0 items-center gap-2">
          <input
            readOnly
            className={`${inputCls} min-w-0`}
            value={overlayUrl}
          />
          <button
            type="button"
            className="shrink-0 rounded-md border ui-divider p-2 ui-text"
            aria-label="Sao chép link"
            onClick={() =>
              Promise.resolve()
                .then(() => navigator.clipboard.writeText(overlayUrl))
                .then(() => toast.success('Đã sao chép link.'))
                .catch(() =>
                  toast.error(
                    'Không sao chép được, hãy chọn và sao chép link thủ công.'
                  )
                )
            }
          >
            <Copy size={16} />
          </button>
          <button
            type="button"
            className="shrink-0 rounded-md border ui-divider p-2 ui-text"
            aria-label="Đổi khoá"
            onClick={() =>
              regenerateMembershipKey()
                .then((res) => {
                  setMine((prev) =>
                    prev ? { ...prev, overlayKey: res.overlayKey } : prev
                  );
                  toast.success('Đã đổi khoá, link cũ ngưng hoạt động.');
                })
                .catch((err) => toast.error(errorMessage(err)))
            }
          >
            <RefreshCw size={16} />
          </button>
        </div>
      )}
      <p className="mt-1 text-[12px] ui-muted">
        Thêm link này vào OBS thành nguồn trình duyệt, phủ toàn màn hình, đặt
        trên cùng.
      </p>

      <p className="mt-4 text-[13px] ui-text">
        {`Tháng này: ${s.joinsThisMonth} lượt tham gia, ${s.activeMembers} hội viên đang có (${s.activeViaCabPlus} người dùng lượt Cab+). Đã nhận ${formatCa(
          s.earnedThisMonthCa
        )}, trong đó ${formatCa(s.pendingCa)} đang chờ hết thời gian giữ.`}
      </p>

      <h4 className="mt-5 font-semibold ui-heading">Danh sách hội viên</h4>
      {members.length === 0 ? (
        <p className="text-[13px] ui-muted">Chưa có hội viên nào.</p>
      ) : (
        <ul className="mt-1 divide-y ui-divider text-[13px] ui-text">
          {members.map((m) => (
            <li
              key={m.userId}
              className="flex flex-wrap justify-between gap-2 py-1"
            >
              <span className="min-w-0 break-words">{m.name}</span>
              <span className="ui-muted">
                {formatMemberUntil(m.expiresAt)}
                {m.lastSource === 'CabPlus' ? ' · lượt Cab+' : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
      {members.length < total && (
        <button
          type="button"
          disabled={loadingMore}
          onClick={loadMore}
          className="mt-2 rounded-md border ui-divider px-3 py-1 text-[13px] ui-text disabled:opacity-60"
        >
          Xem thêm
        </button>
      )}
    </div>
  );
};

export default MembershipSetting;
